export type CardBrand = "visa" | "mastercard" | "amex" | "discover" | "troy";

export interface FauxCreditCardProps {
  /** Last 4 digits (or full number; only last 4 will be shown). */
  last4: string;
  /** Expiration display, e.g. "12/26". */
  expDisplay: string;
  brand: CardBrand;
  /** Optional name on card (can be shown on front). */
  nameOnAccount?: string;
  className?: string;
}

const BRAND_LABEL: Record<CardBrand, string> = {
  visa: "VISA",
  mastercard: "Mastercard",
  amex: "AMEX",
  discover: "Discover",
  troy: "TROY",
};

/** Masked number display: **** **** **** 1234 (or Amex format). */
function maskedNumber(brand: CardBrand, last4: string): string {
  const digits = String(last4).replace(/\D/g, "").slice(-4);
  if (brand === "amex") {
    const five = String(last4).replace(/\D/g, "").slice(-5);
    return "**** ****** " + (five.length >= 5 ? five : five.padStart(5, "*"));
  }
  return "**** **** **** " + (digits.length >= 4 ? digits : digits.padStart(4, "0"));
}

export function FauxCreditCard({
  last4,
  expDisplay,
  brand,
  nameOnAccount,
  className,
}: FauxCreditCardProps) {
  const displayNumber = maskedNumber(brand, last4);

  return (
    <div
      className={
        "relative overflow-hidden rounded-xl bg-healthsource-navy shadow-md " +
        "min-h-[120px] w-full max-w-[320px] px-5 py-4 flex flex-col justify-between " +
        (className ?? "")
      }
      aria-label={`Card ending in ${String(last4).replace(/\D/g, "").slice(-4)}`}
    >
      <div className="font-mono text-lg tracking-[0.2em] text-white/95">
        {displayNumber}
      </div>
      <div className="flex items-end justify-between">
        <div className="flex flex-col gap-0.5">
          {nameOnAccount && (
            <span className="text-xs uppercase tracking-wider text-white/80 truncate max-w-[180px]">
              {nameOnAccount}
            </span>
          )}
          <span className="font-mono text-sm text-white/90">{expDisplay}</span>
        </div>
        <span className="text-right text-sm font-bold tracking-wider text-white">
          {BRAND_LABEL[brand]}
        </span>
      </div>
    </div>
  );
}
